import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteConfig } from '../data/siteConfig';
import { absoluteAssetUrl, absoluteUrl, defaultOgImage, getSeoForPath, normalizePath } from '../data/seo';
import type { FAQItem } from '../data/faqs';

type SEOProps = {
  title: string;
  description: string;
  path?: string;
  type?: 'website' | 'article' | 'product';
  image?: string;
  breadcrumbs?: { label: string; href?: string }[];
  faqs?: FAQItem[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  preloadImages?: { href: string; fetchPriority?: 'high' | 'low' | 'auto'; as?: 'image' }[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
};

export function SEO({
  title,
  description,
  path = '/',
  type = 'website',
  image,
  breadcrumbs,
  faqs,
  jsonLd,
  preloadImages,
  article
}: SEOProps) {
  const cleanPath = normalizePath(path);
  const routeSeo = getSeoForPath(cleanPath);
  const seoTitle = routeSeo?.title ?? title;
  const seoDescription = routeSeo?.description ?? description;
  const canonicalPath = routeSeo?.canonicalPath ?? cleanPath;
  const canonical = absoluteUrl(canonicalPath);
  const ogType = routeSeo?.ogType ?? type;
  const ogImage = absoluteAssetUrl(image ?? routeSeo?.ogImage ?? routeSeo?.product?.image ?? defaultOgImage);
  const activeFaqs = faqs ?? routeSeo?.faqs;
  const articleData = article ?? routeSeo?.article;

  // LocalBusiness & Corporation Schema for Google Knowledge Graph & Maps sync
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'Corporation', 'Organization'],
    '@id': `${siteConfig.domain}/#organization`,
    name: siteConfig.name,
    legalName: siteConfig.legalName,
    alternateName: siteConfig.alternateNames,
    url: `${siteConfig.domain}/`,
    logo: {
      '@type': 'ImageObject',
      url: absoluteAssetUrl('/zfast-logo.webp'),
      width: '512',
      height: '512'
    },
    image: absoluteAssetUrl('/zfast-logo.webp'),
    description: siteConfig.description,
    slogan: siteConfig.tagline,
    telephone: siteConfig.phone,
    email: siteConfig.email,
    address: {
      '@type': 'PostalAddress',
      ...siteConfig.postalAddress
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: siteConfig.geo.latitude,
      longitude: siteConfig.geo.longitude
    },
    hasMap: siteConfig.hasMap,
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:30',
        closes: '18:30'
      }
    ],
    sameAs: siteConfig.sameAs,
    areaServed: {
      '@type': 'Country',
      name: 'India'
    },
    currenciesAccepted: siteConfig.currenciesAccepted,
    paymentAccepted: siteConfig.paymentAccepted,
    priceRange: siteConfig.priceRange,
    knowsAbout: siteConfig.knowsAbout,
    brand: {
      '@type': 'Brand',
      name: siteConfig.name,
      logo: absoluteAssetUrl('/zfast-logo.webp')
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.phone,
      contactType: 'customer service',
      email: siteConfig.email,
      areaServed: 'IN',
      availableLanguage: ['en', 'hi']
    }
  };

  // WebSite Schema with Sitelinks Searchbox
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteConfig.domain}/#website`,
    name: siteConfig.name,
    alternateName: siteConfig.alternateNames,
    url: `${siteConfig.domain}/`,
    description: siteConfig.description,
    publisher: {
      '@id': `${siteConfig.domain}/#organization`
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteConfig.domain}/products?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  // SiteNavigationElement Schema to guide Google Sitelinks
  const siteNavigationSchema =
    cleanPath === '/'
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: [
            {
              '@type': 'SiteNavigationElement',
              position: 1,
              name: 'Contact Us',
              description: 'Reach ZFAST sales, dealership, and support team in Jaipur, India',
              url: absoluteUrl('/contact')
            },
            {
              '@type': 'SiteNavigationElement',
              position: 2,
              name: 'Animal Feed & Edible Oil Products',
              description: 'Explore cattle feed (Z-10000, Z-30, Z-8000), swine feeds, and pure edible oils',
              url: absoluteUrl('/products')
            },
            {
              '@type': 'SiteNavigationElement',
              position: 3,
              name: 'Our Story',
              description: 'The journey, mission, and agricultural manufacturing background of ZFAST Agritech',
              url: absoluteUrl('/our-story')
            },
            {
              '@type': 'SiteNavigationElement',
              position: 4,
              name: 'Quality & Research',
              description: 'Aflatoxin-controlled testing, lab proximate analysis, and batch quality discipline',
              url: absoluteUrl('/quality-research')
            },
            {
              '@type': 'SiteNavigationElement',
              position: 5,
              name: 'Farmer Corner',
              description: 'Free cattle & pig weight calculators, BCS scoring guides, and farm tools',
              url: absoluteUrl('/farmer-corner')
            },
            {
              '@type': 'SiteNavigationElement',
              position: 6,
              name: 'Dealer & Distributor Partnership',
              description: 'Become an authorized ZFAST distributor or retail dealer in your region',
              url: absoluteUrl('/dealer-partnership')
            }
          ]
        }
      : undefined;

  // WebPage Schema
  const webPageSchema = routeSeo
    ? {
        '@context': 'https://schema.org',
        '@type': routeSeo.schemaType === 'Product' ? 'WebPage' : routeSeo.schemaType ?? 'WebPage',
        '@id': `${canonical}#webpage`,
        name: seoTitle,
        description: seoDescription,
        url: canonical,
        isPartOf: {
          '@id': `${siteConfig.domain}/#website`
        },
        about: {
          '@id': `${siteConfig.domain}/#organization`
        }
      }
    : undefined;

  // FAQPage Schema for rich expandable Q&A search snippets & People Also Ask
  const faqSchema =
    activeFaqs && activeFaqs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: activeFaqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
            }
          }))
        }
      : undefined;

  // Enhanced Product Schema
  const productSchema = routeSeo?.product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: routeSeo.product.name,
        image: absoluteAssetUrl(routeSeo.product.image ?? defaultOgImage),
        description: routeSeo.product.description ?? seoDescription,
        category: routeSeo.product.category,
        sku: routeSeo.product.sku,
        brand: {
          '@type': 'Brand',
          name: siteConfig.name
        },
        manufacturer: {
          '@type': 'Organization',
          name: siteConfig.name,
          url: `${siteConfig.domain}/`
        },
        countryOfOrigin: 'IN',
        audience: {
          '@type': 'Audience',
          audienceType: 'Dairy Farmers, Swine Breeders, Agritech Dealers, Wholesale Distributors'
        },
        offers: {
          '@type': 'Offer',
          url: canonical,
          priceCurrency: routeSeo.product.priceCurrency ?? 'INR',
          price: '0.00',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'INR',
            valueAddedTaxIncluded: true
          },
          availability: routeSeo.product.availability ?? 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: {
            '@type': 'Organization',
            name: siteConfig.name
          }
        },
        url: canonical
      }
    : undefined;

  // Breadcrumbs Schema
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: absoluteUrl('/')
            },
            ...breadcrumbs.map((breadcrumb, index) => ({
              '@type': 'ListItem',
              position: index + 2,
              name: breadcrumb.label,
              ...(breadcrumb.href ? { item: absoluteUrl(breadcrumb.href) } : {})
            }))
          ]
        }
      : undefined;

  // Article / BlogPosting Schema
  const articleSchema =
    ogType === 'article' || routeSeo?.schemaType === 'Article' || articleData
      ? {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          '@id': `${canonical}#article`,
          headline: seoTitle,
          description: seoDescription,
          image: ogImage,
          datePublished: articleData?.publishedTime,
          dateModified: articleData?.modifiedTime || articleData?.publishedTime,
          author: {
            '@type': 'Person',
            name: articleData?.author || 'ZFAST Agritech Nutrition Team'
          },
          publisher: {
            '@id': `${siteConfig.domain}/#organization`
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonical
          },
          keywords: articleData?.tags?.join(', ')
        }
      : undefined;

  const schemas = [
    localBusinessSchema,
    websiteSchema,
    siteNavigationSchema,
    webPageSchema,
    articleSchema,
    faqSchema,
    productSchema,
    breadcrumbSchema,
    ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [])
  ];

  const compactSchemas = schemas.filter(Boolean);

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={siteConfig.name} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={ogImage} />

      {/* Article Specific Meta Tags */}
      {ogType === 'article' && articleData?.publishedTime && (
        <meta property="article:published_time" content={articleData.publishedTime} />
      )}
      {ogType === 'article' && (articleData?.modifiedTime || articleData?.publishedTime) && (
        <meta property="article:modified_time" content={articleData.modifiedTime || articleData.publishedTime} />
      )}
      {ogType === 'article' && articleData?.author && (
        <meta property="article:author" content={articleData.author} />
      )}
      {ogType === 'article' && articleData?.section && (
        <meta property="article:section" content={articleData.section} />
      )}
      {ogType === 'article' && articleData?.tags?.map((tag, i) => (
        <meta key={i} property="article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Schema Markup */}
      {compactSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Image Preloads */}
      {preloadImages?.map((img, idx) => (
        <link key={idx} rel="preload" as={img.as || 'image'} href={img.href} fetchPriority={img.fetchPriority} />
      ))}
    </Helmet>
  );
}

